import { List, fromJS } from 'immutable'
import type { ReactElement } from 'react'
import { defaults } from 'lodash-es'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import cc from 'classcat'

import type { BlockAnimation } from './BlockAnimationSettings'
import { DragSource } from './utils/DragAndDrop'
import { blockSupportsHeadline, isHeadlineHidden } from './Workspace.helpers'
import { blockTypes } from './blocks'
import { emptyHeadline } from '../../../components/editorSidebarBlockTemplates'
import { getPlain } from '../../../store/utils'
import { useTranslate } from '../../../utils/translate'
import BlockAnimationSettings from './BlockAnimationSettings'
import BlockColorSettings from './BlockColorSettings'

export type BackgroundColorSettings = {
  backgroundColor: string
  colorOrGradient: 'color' | 'gradient'
  gradientStartColor: string
  gradientMidColor: string
  gradientEndColor: string
  gradientOrientation: '45deg' | '90deg' | '180deg'
  showGradientMidColor: boolean
}

export const defaultBackgroundColorSettings: BackgroundColorSettings = {
  backgroundColor: 'transparent',
  colorOrGradient: 'color',
  gradientStartColor: '#ffffff',
  gradientMidColor: '#828282',
  gradientEndColor: '#000000',
  gradientOrientation: '90deg',
  showGradientMidColor: false,
}

type Props = {
  block: ImmutableMap
  blockPosition: number
  isDragActive: boolean
  config: any
  editorView: boolean
  onChange: (block: ImmutableMap) => void
  onDelete: () => void
}

export default function Block({
  block,
  blockPosition,
  isDragActive,
  config,
  editorView,
  onChange,
  onDelete,
}: Readonly<Props>): ReactElement {
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const t = useTranslate('interface', 'components.elementContextBarComponent')

  const backgroundColorSettings = block.get('backgroundColorSettings')
  const legacyBackgroundColorSetting = block.get('backgroundColor')

  const initialBackgroundColorSettings = useMemo(() => {
    return defaults(
      {},
      getPlain(backgroundColorSettings),
      { backgroundColor: legacyBackgroundColorSetting },
      defaultBackgroundColorSettings,
    )
  }, [backgroundColorSettings, legacyBackgroundColorSetting])

  const [currentBackgroundColorSettings, setCurrentBackgroundColorSettings] =
    useState<BackgroundColorSettings>(initialBackgroundColorSettings)

  const {
    backgroundColor,
    gradientStartColor,
    gradientMidColor,
    gradientEndColor,
    gradientOrientation,
    colorOrGradient,
    showGradientMidColor,
  } = currentBackgroundColorSettings

  const saveBackground = () => {
    onChange(
      block.set('backgroundColorSettings', {
        backgroundColor,
        gradientStartColor,
        gradientMidColor,
        gradientEndColor,
        gradientOrientation,
        colorOrGradient,
        showGradientMidColor,
      }),
    )
  }

  const blockRef = useRef<HTMLElement>(undefined)
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true })

  // Use `useCallback` so we don't recreate the function on each render
  const setRefs = useCallback(
    (node) => {
      // Ref's from useRef needs to have the node assigned to `current`
      blockRef.current = node
      // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
      inViewRef(node)
    },
    [inViewRef],
  )

  const [animation, setAnimation] = useState<BlockAnimation>(block.get('blockAnimation', 'none'))
  const [isAnimatingInEditor, setIsAnimatingInEditor] = useState(false)
  const handleAnimationChange = (animation: BlockAnimation) => {
    setAnimation(animation)
    return handleAnimationReplay()
  }
  const handleAnimationReplay = () =>
    new Promise<void>((resolve) => {
      if (blockRef.current) {
        setIsAnimatingInEditor(true)
        blockRef.current.addEventListener(
          'animationend',
          () => {
            setIsAnimatingInEditor(false)
            resolve()
          },
          { once: true },
        )
      } else resolve()
    })
  const saveAnimationChange = () => onChange(block.set('blockAnimation', animation))

  const actionBarRef = useRef<HTMLDivElement>(null)

  // Build a string representation of the block structure. Examples:
  // epages.image
  // image+text-image+text-image+text
  const blockType: string = (block.getIn(['data', 'columns']) || List()).reduce(
    (blockType: string, column: ImmutableMap) =>
      (blockType && blockType + '-') + (column.has('type') ? column.get('type') : column.keySeq().toJS().join('+')),
    '',
  )

  const BlockComponent = blockTypes[block.get('type')]
  if (!BlockComponent) return <div>{'Unknown block type "' + block.get('type') + '"'}</div>

  const hasHeadlineElement = blockSupportsHeadline(block.get('data'))
  const hasHiddenHeadline = isHeadlineHidden(block.get('data'))

  function handleHeadlineToggleButtonClick() {
    if (block.hasIn(['data', 'headline'])) {
      onChange(block.setIn(['data', 'headline', 'data', 'isHidden'], !hasHiddenHeadline))
    } else {
      // Existing shops do not have the "headline" property in the data.
      const initialHeadline = fromJS(emptyHeadline).setIn(['data', 'isHidden'], !hasHiddenHeadline)
      onChange(block.setIn(['data', 'headline'], initialHeadline))
    }
  }

  const handleDataChange = (data: unknown) => onChange(block.set('data', data))

  return (
    <div
      id={`block-${block.get('_id')}`}
      data-testid={`block-${block.get('_id')}`}
      className={cc([
        'dali-block',
        {
          'block-background': backgroundColor !== 'transparent' || colorOrGradient === 'gradient',
          'drag-active': isDragActive,
          // Only animate when explicitly triggered (editor) / when in view (storefront)
          [animation]: animation !== 'none' && (editorView ? isAnimatingInEditor : inView),
          'will-fade': animation !== 'none' && !editorView, // avoid flash of content before animation starts (storefront)
          'has-animation': animation !== 'none' && editorView, // for styling purposes (editor)
        },
      ])}
      data-block-type={blockType}
      style={{
        backgroundColor: colorOrGradient === 'color' ? backgroundColor : undefined,
        backgroundImage:
          colorOrGradient === 'color'
            ? undefined
            : showGradientMidColor
              ? `linear-gradient( ${gradientOrientation}, ${gradientStartColor}, ${gradientMidColor}, ${gradientEndColor})`
              : `linear-gradient( ${gradientOrientation}, ${gradientStartColor}, ${gradientEndColor})`,
        backgroundOrigin: 'border-box',
      }}
      ref={setRefs}
    >
      {editorView && (
        <div ref={actionBarRef} className="dali-block-actionbar">
          {!confirmingDelete && (
            <DragSource
              type="dali-add"
              className="dali-block-actionbar-button dali-block-actionbar-button-move"
              title={t('moveButton.label')}
              onDrag={() => block}
              blockPosition={blockPosition}
              createPreviewNode={() => {
                const { width = 10 } = actionBarRef.current?.getBoundingClientRect() || {}

                const div = document.createElement('div')
                div.className = 'dali-block-actionbar'
                div.style.width = `${width}px`
                div.style.visibility = 'visible'
                div.style.opacity = '1'

                const div2 = document.createElement('div')
                div2.className = 'dali-block-actionbar-button dali-block-actionbar-button-move'
                div.append(div2)

                return div
              }}
              previewNodeOffset={() => {
                const { width = 10, height = 10 } = actionBarRef.current?.getBoundingClientRect() || {}

                return { x: width / 2, y: height / 2 }
              }}
            />
          )}
          {!confirmingDelete && (
            <div
              className="dali-block-actionbar-button dali-block-actionbar-button-delete"
              title={t('deleteButton.label')}
              onClick={() => setConfirmingDelete(true)}
            />
          )}
          {confirmingDelete && <span>{t('deleteButton.warningMessage')}</span>}
          {confirmingDelete && (
            <div className="dali-block-actionbar-button dali-block-actionbar-button-delete-confirm" onClick={onDelete}>
              <span>{t('deleteConfirmButton.label')}</span>
            </div>
          )}
          {confirmingDelete && (
            <div
              className="dali-block-actionbar-button dali-block-actionbar-button-delete-cancel"
              onClick={() => setConfirmingDelete(false)}
            >
              <span>{t('deleteDismissButton.label')}</span>
            </div>
          )}
          {!confirmingDelete && <div className="dali-block-actionbar-button-separator"></div>}
          {!confirmingDelete && (
            <BlockColorSettings
              currentSettings={currentBackgroundColorSettings}
              onSettingsChange={setCurrentBackgroundColorSettings}
              onCancel={() => setCurrentBackgroundColorSettings(initialBackgroundColorSettings)}
              onSave={saveBackground}
              t={t}
            />
          )}
          {!confirmingDelete && (
            <BlockAnimationSettings
              animation={animation}
              initialAnimation={block.get('blockAnimation', 'none')}
              handleAnimationChange={handleAnimationChange}
              handleAnimationReplay={handleAnimationReplay}
              saveAnimationChange={saveAnimationChange}
              setAnimation={setAnimation}
              t={t}
            />
          )}
          {!confirmingDelete && hasHeadlineElement && (
            <div
              className={cc({
                'dali-block-actionbar-button dali-block-actionbar-button-headline': true,
                'dali-block-actionbar-button-headline-active': !hasHiddenHeadline,
              })}
              onClick={handleHeadlineToggleButtonClick}
              title={t(hasHiddenHeadline ? 'showHeadlineButton.label' : 'hideHeadlineButton.label')}
            />
          )}
        </div>
      )}
      <BlockComponent
        config={config}
        editorView={editorView}
        data={block.get('data')}
        onDataChange={handleDataChange}
        settings={block.get('settings')}
      />
    </div>
  )
}
